import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { X, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { getCategories } from '../../services/categories';
import { getAdminSizeGuides } from '../../services/admin/sizeGuides';
import {
  createProduct,
  updateProduct,
  uploadProductImages,
} from '../../services/admin/products';
import { getApiErrorMessage } from '../../lib/utils';

const createExistingImageItem = (url) => ({ kind: 'existing', url });
const createNewImageItem = (file) => ({
  kind: 'new',
  file,
  preview: URL.createObjectURL(file),
});

const ProductForm = ({ product, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [allCategories, setAllCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imageItems, setImageItems] = useState(() =>
    (product?.images || []).map(createExistingImageItem)
  );
  const [sizeGuides, setSizeGuides] = useState([]);

  // Standard sizes for Turkish market
  const standardSizes = {
    women: ['XS', 'S', 'M', 'L', 'XL', 'XXL', '34', '36', '38', '40', '42', '44'],
    men: ['S', 'M', 'L', 'XL', 'XXL', '46', '48', '50', '52', '54'],
    kids: ['86', '92', '98', '104', '110', '116', '122', '128', '134', '140', '146', '152'],
    baby: ['50', '56', '62', '68', '74', '80'],
  };

  // Standard colors
  const standardColors = [
    { name: 'Beyaz', value: '#FFFFFF' },
    { name: 'Siyah', value: '#000000' },
    { name: 'Gri', value: '#808080' },
    { name: 'Lacivert', value: '#000080' },
    { name: 'Mavi', value: '#0000FF' },
    { name: 'Kırmızı', value: '#FF0000' },
    { name: 'Pembe', value: '#FFC0CB' },
    { name: 'Bej', value: '#F5F5DC' },
    { name: 'Kahverengi', value: '#A52A2A' },
    { name: 'Turuncu', value: '#FFA500' },
    { name: 'Sarı', value: '#FFFF00' },
    { name: 'Yeşil', value: '#008000' },
  ];

  // Create validation schema dynamically to check if subcategories exist
  const validationSchema = Yup.object({
    name: Yup.string().required('Ürün adı zorunludur'),
    description: Yup.string().required('Açıklama zorunludur'),
    price: Yup.number().min(0, 'Fiyat 0\'dan büyük olmalıdır').required('Fiyat zorunludur'),
    category: Yup.string().required('Kategori seçimi zorunludur'),
    subcategory: Yup.string().nullable(),
    stock: Yup.number().min(0, 'Stok 0\'dan küçük olamaz').required('Stok zorunludur'),
  });

  const formik = useFormik({
    initialValues: {
      name: product?.name || '',
      description: product?.description || '',
      price: product?.price || 0,
      originalPrice: product?.originalPrice || null,
      category: product?.category ? String(product.category) : '',
      subcategory: product?.subcategory ? String(product.subcategory) : '',
      sizeGuideId: product?.sizeGuideId ? String(product.sizeGuideId) : '',
      sizes: product?.sizes || [],
      colors: product?.colors || [],
      colorNames: product?.colorNames || [],
      stock: product?.stock || 0,
      material: product?.material || '',
      care: product?.care || '',
      isNew: product?.isNew || false,
      isBestSeller: product?.isBestSeller || false,
      isFeatured: product?.isFeatured || false,
      onSale: product?.onSale || false,
      images: product?.images || [],
    },
    validationSchema,
    onSubmit: async (values) => {
      // Manual validation for subcategory
      const hasSubcategories = allCategories.some((c) => c.parentId === values.category);
      if (hasSubcategories && !values.subcategory) {
        formik.setFieldError('subcategory', 'Alt kategori seçimi zorunludur');
        formik.setFieldTouched('subcategory', true);
        return;
      }

      try {
        setUploadingImages(true);

        const productData = {
          ...values,
          price: parseFloat(values.price),
          originalPrice: values.originalPrice ? parseFloat(values.originalPrice) : null,
          stock: parseInt(values.stock),
          subcategory: values.subcategory || null, // Set to null if empty
        sizeGuideId: values.sizeGuideId || null,
        };

        if (product?.id) {
          const newFiles = imageItems
            .filter((item) => item.kind === 'new')
            .map((item) => item.file);
          let uploadedUrls = [];

          if (newFiles.length > 0) {
            uploadedUrls = await uploadProductImages(newFiles, product.id);
          }

          let uploadIndex = 0;
          productData.images = imageItems
            .map((item) => {
              if (item.kind === 'existing') return item.url;
              const url = uploadedUrls[uploadIndex];
              uploadIndex += 1;
              return url;
            })
            .filter(Boolean);

          await updateProduct(product.id, productData);
          toast({
            title: 'Başarılı',
            description: 'Ürün başarıyla güncellendi.',
          });
        } else {
          const newFiles = imageItems
            .filter((item) => item.kind === 'new')
            .map((item) => item.file);
          const created = await createProduct(productData);

          if (newFiles.length > 0 && created?.id) {
            const uploadedUrls = await uploadProductImages(newFiles, created.id);
            let uploadIndex = 0;
            const finalImages = imageItems
              .map((item) => {
                if (item.kind !== 'new') return null;
                const url = uploadedUrls[uploadIndex];
                uploadIndex += 1;
                return url;
              })
              .filter(Boolean);

            await updateProduct(created.id, {
              ...productData,
              images: finalImages,
            });
          }
          toast({
            title: 'Başarılı',
            description: 'Ürün başarıyla oluşturuldu.',
          });
        }

        onSuccess();
      } catch (error) {
        console.error('Error saving product:', error);
        toast({
          title: 'Hata',
          description: getApiErrorMessage(
            error,
            'Ürün kaydedilirken bir hata oluştu. Görseller yüklenemediyse dosya adını değiştirmeden tekrar deneyin.'
          ),
          variant: 'destructive',
        });
      } finally {
        setUploadingImages(false);
      }
    },
  });

  useEffect(() => {
    return () => {
      imageItems.forEach((item) => {
        if (item.kind === 'new') URL.revokeObjectURL(item.preview);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cleanup previews on unmount
  }, []);

  useEffect(() => {
    const flattenCategories = (items) => {
      const result = [];
      const walk = (list) => {
        list.forEach((item) => {
          result.push({
            ...item,
            id: String(item.id),
            parentId: item.parentId ? String(item.parentId) : null,
          });
          if (item.children?.length) {
            walk(item.children);
          }
        });
      };
      walk(items);
      return result;
    };

    const fetchCategories = async () => {
      try {
        const cats = await getCategories();
        const flat = flattenCategories(cats);
        setAllCategories(flat);
        // Filter only top-level categories (no parentId) for category selection
        const topLevelCategories = flat.filter((cat) => !cat.parentId);
        setCategories(topLevelCategories);
        
        // Set subcategories based on selected category if product has a category
        if (product?.category) {
          const subcats = flat.filter((c) => c.parentId === String(product.category));
          setSubcategories(subcats);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
    const fetchSizeGuides = async () => {
      try {
        const guides = await getAdminSizeGuides();
        setSizeGuides(guides);
      } catch (error) {
        console.error('Error fetching size guides:', error);
      }
    };
    fetchSizeGuides();
  }, [product?.category]);

  // Update subcategories when category changes
  useEffect(() => {
    if (formik.values.category && allCategories.length > 0) {
      // Find subcategories where parentId matches selected category
      const subcats = allCategories.filter((c) => c.parentId === formik.values.category);
      setSubcategories(subcats);
      // Clear subcategory selection when category changes
      if (formik.values.subcategory) {
        formik.setFieldValue('subcategory', '');
      }
    } else {
      setSubcategories([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formik intentionally excluded to avoid re-run loops
  }, [formik.values.category, allCategories]);

  const handleCategoryChange = (categoryId) => {
    formik.setFieldValue('category', categoryId);
    formik.setFieldValue('subcategory', '');
    // Find subcategories where parentId matches selected category
    const subcats = allCategories.filter((c) => c.parentId === categoryId);
    setSubcategories(subcats);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setImageItems((prev) => [...prev, ...files.map(createNewImageItem)]);
    e.target.value = '';
  };

  const removeImageItem = (index) => {
    setImageItems((prev) => {
      const item = prev[index];
      if (item?.kind === 'new') URL.revokeObjectURL(item.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const moveImageItem = (index, direction) => {
    setImageItems((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const toggleSize = (size) => {
    const currentSizes = formik.values.sizes || [];
    if (currentSizes.includes(size)) {
      formik.setFieldValue('sizes', currentSizes.filter((s) => s !== size));
    } else {
      formik.setFieldValue('sizes', [...currentSizes, size]);
    }
  };

  const toggleColor = (color, colorName) => {
    const currentColors = formik.values.colors || [];
    const currentColorNames = formik.values.colorNames || [];
    const index = currentColors.indexOf(color);

    if (index > -1) {
      formik.setFieldValue('colors', currentColors.filter((_, i) => i !== index));
      formik.setFieldValue('colorNames', currentColorNames.filter((_, i) => i !== index));
    } else {
      formik.setFieldValue('colors', [...currentColors, color]);
      formik.setFieldValue('colorNames', [...currentColorNames, colorName]);
    }
  };

  const availableSizes = standardSizes[formik.values.category] || standardSizes.women;

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Temel Bilgiler</h3>

        <div>
          <Label htmlFor="name">Ürün Adı *</Label>
          <Input
            id="name"
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={formik.errors.name && formik.touched.name ? 'border-red-500' : ''}
          />
          {formik.errors.name && formik.touched.name && (
            <p className="text-red-500 text-sm mt-1">{formik.errors.name}</p>
          )}
        </div>

        <div>
          <Label htmlFor="description">Açıklama *</Label>
          <Textarea
            id="description"
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            rows={6}
            placeholder="Ürün açıklamasını girin. HTML kullanabilirsiniz (örn: &lt;br&gt; satır atlamak için, &lt;strong&gt;kalın&lt;/strong&gt; metin için)"
            className={`mt-1 ${formik.errors.description && formik.touched.description ? 'border-red-500' : ''}`}
          />
          {formik.errors.description && formik.touched.description && (
            <p className="text-red-500 text-sm mt-1">{formik.errors.description}</p>
          )}
          <p className="text-sm text-gray-500 mt-1">
            HTML etiketleri kullanabilirsiniz (örn: &lt;p&gt;, &lt;br&gt;, &lt;strong&gt;, &lt;ul&gt;, &lt;li&gt;)
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="price">Fiyat (₺) *</Label>
            <Input
              id="price"
              name="price"
              type="number"
              step="0.01"
              value={formik.values.price}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={formik.errors.price && formik.touched.price ? 'border-red-500' : ''}
            />
            {formik.errors.price && formik.touched.price && (
              <p className="text-red-500 text-sm mt-1">{formik.errors.price}</p>
            )}
          </div>

          <div>
            <Label htmlFor="originalPrice">Eski Fiyat (₺) - İndirimli ise</Label>
            <Input
              id="originalPrice"
              name="originalPrice"
              type="number"
              step="0.01"
              value={formik.values.originalPrice || ''}
              onChange={formik.handleChange}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="category">Kategori *</Label>
            <Select
              value={formik.values.category || ''}
              onValueChange={handleCategoryChange}
            >
              <SelectTrigger id="category">
                <SelectValue placeholder="Kategori seçin" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={String(cat.id)}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formik.errors.category && formik.touched.category && (
              <p className="text-red-500 text-sm mt-1">{formik.errors.category}</p>
            )}
          </div>

          <div>
            <Label htmlFor="subcategory">
              Alt Kategori {subcategories.length > 0 ? '*' : ''}
            </Label>
            <Select
              value={formik.values.subcategory || ''}
              onValueChange={(value) => {
                formik.setFieldValue('subcategory', value || '');
                formik.setFieldTouched('subcategory', true);
              }}
              disabled={!formik.values.category || subcategories.length === 0}
            >
              <SelectTrigger id="subcategory">
                <SelectValue placeholder={subcategories.length === 0 ? 'Bu kategorinin alt kategorisi yok' : 'Alt kategori seçin'} />
              </SelectTrigger>
              <SelectContent>
                {subcategories.length > 0 ? (
                  subcategories.map((sub) => (
                    <SelectItem key={sub.id} value={String(sub.id)}>
                      {sub.name}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="none" disabled>
                    Alt kategori bulunmuyor
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {formik.errors.subcategory && formik.touched.subcategory && (
              <p className="text-red-500 text-sm mt-1">{formik.errors.subcategory}</p>
            )}
            {subcategories.length === 0 && formik.values.category && (
              <p className="text-sm text-gray-500 mt-1">Bu kategorinin alt kategorisi bulunmuyor</p>
            )}
          </div>
        </div>

        <div>
          <Label htmlFor="sizeGuideId">Beden Rehberi</Label>
          <Select
            value={formik.values.sizeGuideId || 'none'}
            onValueChange={(value) => {
              formik.setFieldValue('sizeGuideId', value === 'none' ? '' : value);
            }}
          >
            <SelectTrigger id="sizeGuideId">
              <SelectValue placeholder="Beden rehberi seçin (opsiyonel)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Beden rehberi yok</SelectItem>
              {sizeGuides.map((guide) => (
                <SelectItem key={guide.id} value={String(guide.id)}>
                  {guide.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="stock">Stok Miktarı *</Label>
          <Input
            id="stock"
            name="stock"
            type="number"
            min="0"
            value={formik.values.stock}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={formik.errors.stock && formik.touched.stock ? 'border-red-500' : ''}
          />
          {formik.errors.stock && formik.touched.stock && (
            <p className="text-red-500 text-sm mt-1">{formik.errors.stock}</p>
          )}
        </div>
      </div>

      {/* Images */}
      <div className="space-y-4">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold">Ürün Görselleri</h3>
            <p className="text-sm text-gray-500">
              Görseller 1&apos;den başlayarak numaralandırılır. 1. görsel vitrin/kapak fotoğrafıdır.
            </p>
          </div>
          {imageItems.length > 0 && (
            <p className="text-sm font-medium text-gray-700">
              Toplam {imageItems.length} görsel
            </p>
          )}
        </div>

        {imageItems.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {imageItems.map((item, index) => (
              <div
                key={item.kind === 'existing' ? `${item.url}-${index}` : `${item.preview}-${index}`}
                className="relative rounded-lg border bg-gray-50 p-2"
              >
                <div className="absolute left-3 top-3 z-10 flex h-7 min-w-[1.75rem] items-center justify-center rounded-full bg-[#1a1a2e] px-2 text-xs font-semibold text-white">
                  {index + 1}
                </div>
                {index === 0 && (
                  <div className="absolute left-3 top-12 z-10 rounded bg-white/90 px-2 py-0.5 text-[10px] font-medium text-gray-700 shadow-sm">
                    Kapak
                  </div>
                )}
                <img
                  src={item.kind === 'existing' ? item.url : item.preview}
                  alt={`Ürün görseli ${index + 1}`}
                  className="h-40 w-full rounded object-cover"
                />
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-500 truncate">
                    {item.kind === 'new' ? item.file.name : 'Mevcut görsel'}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      disabled={index === 0}
                      onClick={() => moveImageItem(index, -1)}
                      aria-label={`Görsel ${index + 1} yukarı taşı`}
                    >
                      <ChevronUp size={16} />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      disabled={index === imageItems.length - 1}
                      onClick={() => moveImageItem(index, 1)}
                      aria-label={`Görsel ${index + 1} aşağı taşı`}
                    >
                      <ChevronDown size={16} />
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => removeImageItem(index)}
                      aria-label={`Görsel ${index + 1} sil`}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div>
          <Label htmlFor="images">Görsel Ekle</Label>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              id="images"
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="flex-1"
            />
            <p className="text-xs text-gray-500 sm:max-w-xs">
              Birden fazla seçerseniz sırayla {imageItems.length + 1}, {imageItems.length + 2}…
              numarasıyla eklenir.
            </p>
          </div>
        </div>
      </div>

      {/* Sizes */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Bedenler</h3>
        <div className="flex flex-wrap gap-2">
          {availableSizes.map((size) => (
            <button
              key={size}
              type="button"
              onClick={() => toggleSize(size)}
              className={`px-4 py-2 border rounded-md transition-colors ${
                formik.values.sizes?.includes(size)
                  ? 'bg-black text-white border-black'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              {size}
            </button>
          ))}
        </div>
      </div>

      {/* Colors */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Renkler</h3>
        <div className="flex flex-wrap gap-2">
          {standardColors.map((color) => (
            <button
              key={color.value}
              type="button"
              onClick={() => toggleColor(color.value, color.name)}
              className={`w-12 h-12 rounded-full border-2 transition-all ${
                formik.values.colors?.includes(color.value)
                  ? 'border-black scale-110'
                  : 'border-gray-200 hover:border-gray-400'
              }`}
              style={{ backgroundColor: color.value }}
              title={color.name}
            />
          ))}
        </div>
        {formik.values.colors?.length > 0 && (
          <p className="text-sm text-gray-600">
            Seçilen renkler: {formik.values.colorNames?.join(', ')}
          </p>
        )}
      </div>

      {/* Additional Info */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Ek Bilgiler</h3>

        <div>
          <Label htmlFor="material">Malzeme</Label>
          <Input
            id="material"
            name="material"
            value={formik.values.material}
            onChange={formik.handleChange}
            placeholder="Örn: %100 Pamuk"
          />
        </div>

        <div>
          <Label htmlFor="care">Bakım Talimatları</Label>
          <Input
            id="care"
            name="care"
            value={formik.values.care}
            onChange={formik.handleChange}
            placeholder="Örn: Soğuk suda yıkayın"
          />
        </div>
      </div>

      {/* Flags */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Özellikler</h3>
        <div className="flex flex-col gap-3">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="isNew"
              checked={formik.values.isNew}
              onCheckedChange={(checked) => formik.setFieldValue('isNew', checked)}
            />
            <Label htmlFor="isNew">Yeni Ürün</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="isBestSeller"
              checked={formik.values.isBestSeller}
              onCheckedChange={(checked) => formik.setFieldValue('isBestSeller', checked)}
            />
            <Label htmlFor="isBestSeller">Çok Satan</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="isFeatured"
              checked={formik.values.isFeatured}
              onCheckedChange={(checked) => formik.setFieldValue('isFeatured', checked)}
            />
            <Label htmlFor="isFeatured">Haftanın Ürünü</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="onSale"
              checked={formik.values.onSale}
              onCheckedChange={(checked) => formik.setFieldValue('onSale', checked)}
            />
            <Label htmlFor="onSale">İndirimde</Label>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-4 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          İptal
        </Button>
        <Button type="submit" disabled={uploadingImages}>
          {uploadingImages ? 'Kaydediliyor...' : product ? 'Güncelle' : 'Oluştur'}
        </Button>
      </div>
    </form>
  );
};

export default ProductForm;
